import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Check, Search, X, Upload, ClipboardList, ExternalLink } from "lucide-react";
import { api, openExternal } from "../lib/api";
import { AppText, Button, Card, Header, Toggle } from "../components/ui";
import { BLOCKS, QUESTIONS } from "../lib/questions";
import { Seo } from "../components/Seo";

const STORAGE_KEY = "km1_profile_draft";
const ANON_KEY = "km1_anon_id";

export default function Profil() {
  const navigate = useNavigate();
  const [answers, setAnswers] = useState({});
  const [step, setStep] = useState(0);
  const [ready, setReady] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [email, setEmail] = useState("");
  const [firstName, setFirstName] = useState("");
  const [consent, setConsent] = useState(false);
  const [mode, setMode] = useState(null); // null = écran d'accueil (bilan ou questionnaire)

  const visible = useMemo(() => QUESTIONS.filter((q) => !q.showIf || q.showIf(answers)), [answers]);
  const total = visible.length + 1;
  const isEmailStep = step >= visible.length;
  const q = visible[step];

  useEffect(() => {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (raw) {
        const d = JSON.parse(raw);
        setAnswers(d.answers || {});
        setStep(Math.min(d.step || 0, QUESTIONS.length));
        if ((d.step || 0) > 0 || Object.keys(d.answers || {}).length) setMode("quiz");
      }
      if (!localStorage.getItem(ANON_KEY)) {
        localStorage.setItem(ANON_KEY, `anon_${Math.random().toString(36).slice(2, 12)}${Date.now().toString(36)}`);
      }
    } finally {
      setReady(true);
    }
  }, []);

  useEffect(() => {
    if (ready) localStorage.setItem(STORAGE_KEY, JSON.stringify({ answers, step }));
  }, [answers, step, ready]);

  const set = (id, value) => setAnswers((a) => ({ ...a, [id]: value }));
  const value = q ? answers[q.id] : undefined;

  const canNext = !q || (
    (q.type === "number" ? value !== undefined && value !== "" && !Number.isNaN(Number(value)) : true) &&
    (q.type === "choice" ? value !== undefined : true) &&
    (q.type === "multi" ? Array.isArray(value) && value.length > 0 : true) &&
    (q.type === "shoe" ? answers.C1_id !== undefined || answers.C1_choice !== undefined : true)
  );

  const next = () => { if (canNext) setStep((v) => Math.min(v + 1, total - 1)); };
  const back = () => (step > 0 ? setStep((v) => v - 1) : navigate(-1));

  const submit = async (withEmail) => {
    if (withEmail && !/\S+@\S+\.\S+/.test(email.trim())) {
      toast.error("Cet email ne semble pas valide");
      return;
    }
    setSubmitting(true);
    try {
      const anon = localStorage.getItem(ANON_KEY) || `anon_${Date.now()}`;
      const clean = {};
      for (const k of Object.keys(answers)) if (!k.startsWith("C1_")) clean[k] = answers[k];
      clean.C1 = answers.C1_label || answers.C1_choice || null;
      const res = await api.createProfile({
        anon_id: anon,
        answers: clean,
        shoe_id: answers.C1_id || null,
        shoe_label: answers.C1_choice === "saisie" ? answers.C1_label || null : null,
        email: withEmail ? email.trim() : null,
        consent_marketing: withEmail ? consent : false,
        first_name: firstName.trim() || null,
      });
      localStorage.removeItem(STORAGE_KEY);
      localStorage.setItem("km1_last_profile", res.id);
      navigate(`/resultat/${res.id}`);
    } catch (e) {
      toast.error(e.message || "Impossible d'envoyer tes réponses");
    } finally {
      setSubmitting(false);
    }
  };

  if (!ready) return <div className="min-h-screen bg-km-cream" />;
  if (mode === null) return <BilanGate onQuiz={() => setMode("quiz")} navigate={navigate} />;
  const progress = Math.round(((step + 1) / total) * 100);

  return (
    <div className="min-h-screen bg-km-cream flex flex-col">
      <Seo title="Ton profil de coureur en 5 minutes" description="Questionnaire gratuit : découvre si tu es terrien ou aérien, ton score de stabilité, le verdict sur tes chaussures et les 3 erreurs que tu fais probablement." />
      <Header title="Ton profil de coureur" onBack={back} />
      <div className="h-1.5 bg-km-border mx-4 rounded-full overflow-hidden" aria-label={`Progression ${progress}%`}>
        <div className="h-1.5 bg-km-coral rounded-full transition-all duration-300" style={{ width: `${progress}%` }} data-testid="progress-bar" />
      </div>

      <div className="flex-1 overflow-y-auto p-6 pb-40 flex justify-center">
        <div className="w-full max-w-[560px]">
          {q ? (
            <div key={q.id} className="animate-fade-up">
              <AppText variant="label" className="text-km-coral">{BLOCKS[q.block]} · {step + 1}/{total}</AppText>
              <AppText variant="title" className="mt-2" testID="question-title">{q.title}</AppText>
              {q.help ? <AppText variant="body" className="text-km-muted mt-1">{q.help}</AppText> : null}
              <div className="mt-6">
                {q.type === "number" && <NumberInput q={q} value={value} onChange={(v) => set(q.id, v)} onSubmit={next} />}
                {q.type === "choice" && (
                  <div className="flex flex-col gap-2">
                    {q.options.map((o) => (
                      <OptionButton key={o.value} label={o.label} emoji={o.emoji} selected={value === o.value} onClick={() => set(q.id, o.value)} testID={`opt-${q.id}-${o.value}`} />
                    ))}
                  </div>
                )}
                {q.type === "multi" && (
                  <div className={q.cols === 2 ? "grid grid-cols-2 gap-2" : "flex flex-col gap-2"}>
                    {q.options.map((o) => {
                      const arr = Array.isArray(value) ? value : [];
                      const sel = arr.includes(o.value);
                      return <OptionButton key={o.value} label={o.label} emoji={o.emoji} selected={sel} testID={`opt-${q.id}-${o.value}`}
                        onClick={() => set(q.id, sel ? arr.filter((x) => x !== o.value) : [...arr, o.value])} />;
                    })}
                  </div>
                )}
                {q.type === "shoe" && <ShoePicker answers={answers} set={set} />}
              </div>
            </div>
          ) : (
            <EmailStep email={email} setEmail={setEmail} firstName={firstName} setFirstName={setFirstName} consent={consent} setConsent={setConsent} />
          )}
        </div>
      </div>

      <div className="fixed bottom-0 left-0 right-0 bg-km-cream border-t border-km-divider px-6 pt-2 pb-6 flex justify-center">
        <div className="w-full max-w-[560px]">
          {isEmailStep ? (
            <>
              <Button title="Voir mon profil et recevoir le rapport" onClick={() => submit(true)} loading={submitting} testID="submit-with-email" className="w-full" />
              <button onClick={() => submit(false)} disabled={submitting} data-testid="submit-without-email"
                className="w-full min-h-[44px] flex items-center justify-center mt-2 font-text text-[14px] text-km-muted hover:text-km-coral">
                Voir le résultat sans donner mon email
              </button>
            </>
          ) : (
            <Button title="Suivant" onClick={next} disabled={!canNext} testID="next-button" className="w-full" />
          )}
        </div>
      </div>
    </div>
  );
}

function OptionButton({ label, emoji, selected, onClick, testID }) {
  return (
    <button onClick={onClick} data-testid={testID} role="radio" aria-checked={selected}
      className={`flex items-center gap-3 min-h-[60px] rounded-2xl border-2 px-4 py-3 text-left transition-all active:scale-[0.99] ${selected ? "border-km-coral bg-km-coralt" : "border-km-border bg-white hover:border-km-borderstrong"}`}>
      {emoji ? <span className="text-2xl">{emoji}</span> : null}
      <span className={`flex-1 font-text font-bold text-[17px] ${selected ? "text-km-oncoralt" : "text-km-ink"}`}>{label}</span>
      {selected ? <Check size={22} strokeWidth={2.5} className="text-km-coral" /> : null}
    </button>
  );
}

function NumberInput({ q, value, onChange, onSubmit }) {
  return (
    <div className="flex items-center gap-3">
      <input data-testid={`input-${q.id}`} value={value === undefined ? "" : String(value)}
        onChange={(e) => onChange(e.target.value.replace(/[^0-9]/g, ""))}
        onKeyDown={(e) => e.key === "Enter" && onSubmit()} inputMode="numeric" placeholder="—" autoFocus
        className="flex-1 min-w-0 h-[72px] bg-white rounded-2xl border-2 border-km-border px-4 font-display font-extrabold text-[34px] text-km-ink outline-none focus:border-km-coral" />
      <AppText variant="subtitle" className="text-km-muted">{q.unit}</AppText>
    </div>
  );
}

function ShoePicker({ answers, set }) {
  const [query, setQuery] = useState("");
  const [results, setResults] = useState([]);
  const [searched, setSearched] = useState(false);
  const selected = !!answers.C1_id || !!answers.C1_choice;

  useEffect(() => {
    if (query.trim().length < 2) { setResults([]); setSearched(false); return; }
    const t = setTimeout(() => api.searchShoes(query).then((r) => { setResults(r); setSearched(true); }).catch(() => setResults([])), 250);
    return () => clearTimeout(t);
  }, [query]);

  const clear = () => { set("C1_id", undefined); set("C1_label", undefined); set("C1_choice", undefined); };
  const pick = (shoe) => { clear(); set("C1_id", shoe.id); set("C1_label", shoe.label); setQuery(""); setResults([]); };
  const useTyped = () => { clear(); set("C1_choice", "saisie"); set("C1_label", query.trim()); setQuery(""); setResults([]); };
  const choose = (v) => { if (answers.C1_choice === v) { clear(); return; } clear(); set("C1_choice", v); setQuery(""); };
  const showNoResult = searched && results.length === 0 && query.trim().length >= 3;

  return (
    <div className="flex flex-col gap-2">
      {selected ? (
        <Card className="flex items-center gap-3 p-4" testID="shoe-selected">
          <Check size={22} strokeWidth={2.5} className="text-km-sage" />
          <div className="flex-1">
            <AppText variant="bodyStrong">
              {answers.C1_id || answers.C1_choice === "saisie" ? answers.C1_label : answers.C1_choice === "aucune" ? "Je n'en ai pas encore" : "Je ne sais pas"}
            </AppText>
            {answers.C1_choice === "saisie" ? <AppText variant="caption" className="text-km-muted">Modèle pas encore dans notre liste : on le note, l'entraîneur vérifiera ses mesures.</AppText> : null}
          </div>
          <button onClick={clear} data-testid="shoe-clear" className="min-w-[44px] min-h-[44px] flex items-center justify-center"><X size={22} strokeWidth={2.5} className="text-km-muted" /></button>
        </Card>
      ) : null}
      <div className="flex items-center gap-2 bg-white rounded-2xl border-2 border-km-border px-4 min-h-[56px]">
        <Search size={20} className="text-km-muted" />
        <input data-testid="shoe-search" value={query}
          onChange={(e) => { setQuery(e.target.value); if (selected) clear(); }}
          placeholder={selected ? "Chercher un autre modèle…" : "ex : Hoka Bondi, Brooks Ghost…"}
          className="flex-1 min-w-0 min-h-[52px] font-text font-semibold text-[17px] text-km-ink bg-transparent outline-none" />
      </div>
      {results.map((r) => (
        <button key={r.id} onClick={() => pick(r)} data-testid={`shoe-result-${r.id}`} className="text-left bg-white rounded-md p-3 border border-km-border min-h-[48px] hover:border-km-coral">
          <AppText variant="bodyStrong">{r.label}</AppText>
          <AppText variant="caption" className="text-km-muted">{r.stack_mm ? `${r.stack_mm} mm · drop ${r.drop_mm} mm` : ""}</AppText>
        </button>
      ))}
      {showNoResult ? (
        <button onClick={useTyped} data-testid="shoe-use-typed" className="text-left bg-white rounded-md p-3 border-2 border-dashed border-km-coral min-h-[48px]">
          <AppText variant="bodyStrong">Utiliser « {query.trim()} »</AppText>
          <AppText variant="caption" className="text-km-muted">Ce modèle n'est pas encore dans notre liste. On le garde tel quel : ton bilan dira « à vérifier » et l'entraîneur complétera ses mesures.</AppText>
        </button>
      ) : null}
      <div className="flex flex-col gap-2 mt-3">
        <OptionButton label="Je ne sais pas" emoji="🤷" selected={answers.C1_choice === "je_ne_sais_pas"} onClick={() => choose("je_ne_sais_pas")} testID="opt-C1-unknown" />
        <OptionButton label="Je n'en ai pas encore" emoji="🛒" selected={answers.C1_choice === "aucune"} onClick={() => choose("aucune")} testID="opt-C1-none" />
      </div>
      <AppText variant="caption" className="text-km-muted">Appuie à nouveau sur une réponse pour la décocher. La photo de ta chaussure pourra être ajoutée plus tard depuis ton espace.</AppText>
    </div>
  );
}

function EmailStep({ email, setEmail, firstName, setFirstName, consent, setConsent }) {
  return (
    <div className="animate-fade-up">
      <AppText variant="label" className="text-km-sage">Dernière étape</AppText>
      <AppText variant="title" className="mt-2" testID="question-title">Ton profil est prêt. Où t'envoie-t-on le rapport ?</AppText>
      <AppText variant="body" className="text-km-muted mt-1">Le résultat s'affiche tout de suite. Le rapport PDF et la suite arrivent par email.</AppText>
      <input data-testid="input-firstname" value={firstName} onChange={(e) => setFirstName(e.target.value)} placeholder="Ton prénom (facultatif)"
        className="w-full mt-6 min-h-[52px] bg-white rounded-2xl border-2 border-km-border px-4 font-text font-semibold text-[17px] text-km-ink outline-none focus:border-km-coral" />
      <input data-testid="input-email" value={email} onChange={(e) => setEmail(e.target.value)} type="email" placeholder="ton@email.fr" autoComplete="email"
        className="w-full mt-2 min-h-[52px] bg-white rounded-2xl border-2 border-km-border px-4 font-text font-semibold text-[17px] text-km-ink outline-none focus:border-km-coral" />
      <button onClick={() => setConsent(!consent)} data-testid="consent-toggle" type="button" className="flex items-center gap-3 mt-4 min-h-[48px] text-left">
        <Toggle checked={consent} />
        <AppText variant="caption" className="text-km-ontertiary flex-1">J'accepte de recevoir des conseils de KM01 (désinscription en un clic).</AppText>
      </button>
    </div>
  );
}


function BilanGate({ onQuiz, navigate }) {
  const inputRef = useRef(null);
  const [showUpload, setShowUpload] = useState(false);
  const [firstName, setFirstName] = useState("");
  const [email, setEmail] = useState("");
  const [consent, setConsent] = useState(false);
  const [busy, setBusy] = useState(false);
  const field = "w-full min-h-[48px] bg-white rounded-xl border-2 border-km-border px-3 font-text text-[16px] text-km-ink outline-none focus:border-km-coral";

  const onFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    if (!file.name.toLowerCase().endsWith(".pdf")) { toast.error("Envoie ton bilan au format PDF"); return; }
    if (email && !/^\S+@\S+\.\S+$/.test(email)) { toast.error("Email invalide"); return; }
    setBusy(true);
    const id = toast.loading("Lecture de ton bilan en cours…");
    try {
      const anon = localStorage.getItem(ANON_KEY) || `anon_${Date.now()}`;
      const res = await api.profileFromBilan(file, { anonId: anon, firstName: firstName.trim(), email: email.trim(), consent });
      localStorage.removeItem(STORAGE_KEY);
      localStorage.setItem("km1_last_profile", res.id);
      toast.success("Bilan lu — voici ton profil de coureur", { id });
      navigate(`/resultat/${res.id}`);
    } catch (err) {
      toast.error(err.message || "Import impossible", { id });
    } finally { setBusy(false); }
  };


  return (
    <div className="min-h-screen bg-km-cream flex flex-col">
      <Seo title="Ton profil de coureur" description="Découvre ton profil de coureur : réponds au questionnaire gratuit ou importe ton bilan d'analyse de foulée liberetafoulee.fr." />
      <Header title="Ton profil de coureur" onBack={() => navigate("/")} />
      <div className="flex-1 overflow-y-auto p-6 flex justify-center">
        <div className="w-full max-w-[560px] animate-fade-up">
          <AppText variant="display">On y va ?</AppText>
          <AppText variant="body" className="text-km-ontertiary mt-2">Deux façons de créer ton profil. Le plus rapide si tu as déjà fait ton analyse de foulée : importe ton bilan, on a déjà toutes tes réponses.</AppText>

          <button onClick={() => setShowUpload(true)} data-testid="gate-bilan" className="w-full text-left mt-6">
            <Card className={`flex items-start gap-3 p-5 border-2 transition-colors ${showUpload ? "border-km-coral" : "border-km-border"}`}>
              <Upload size={22} className="text-km-coral shrink-0 mt-0.5" />
              <div>
                <AppText variant="bodyStrong">J'ai déjà fait mon analyse de foulée</AppText>
                <AppText variant="caption" className="text-km-muted mt-1">Importe ton bilan PDF liberetafoulee.fr — pas besoin de répondre aux questions, tout y est déjà.</AppText>
              </div>
            </Card>
          </button>

          {showUpload ? (
            <Card className="flex flex-col gap-3 mt-3" testID="gate-upload-form">
              <input ref={inputRef} type="file" accept="application/pdf,.pdf" onChange={onFile} className="hidden" data-testid="gate-file-input" />
              <div>
                <AppText variant="label" className="text-km-muted mb-1">Ton prénom (facultatif)</AppText>
                <input value={firstName} onChange={(e) => setFirstName(e.target.value)} placeholder="Prénom" className={field} data-testid="gate-firstname" />
              </div>
              <div>
                <AppText variant="label" className="text-km-muted mb-1">Ton email (facultatif — pour recevoir ton profil)</AppText>
                <input value={email} onChange={(e) => setEmail(e.target.value)} type="email" placeholder="ton@email.fr" className={field} data-testid="gate-email" />
              </div>
              {email ? (
                <button onClick={() => setConsent((v) => !v)} className="flex items-center gap-2 text-left" data-testid="gate-consent">
                  <Toggle checked={consent} />
                  <AppText variant="caption" className="text-km-muted flex-1">J'accepte de recevoir mon profil et des conseils par email.</AppText>
                </button>
              ) : null}
              <Button title="Importer mon bilan PDF" onClick={() => inputRef.current?.click()} loading={busy} testID="gate-upload-btn" icon={<Upload size={18} strokeWidth={2.5} />} />
              <button onClick={() => openExternal("https://liberetafoulee.fr")} className="flex items-center gap-2 self-start text-km-sage font-bold min-h-[40px]" data-testid="gate-get-link">
                <ExternalLink size={16} /> Je n'ai pas encore fait mon analyse
              </button>
            </Card>
          ) : null}

          <button onClick={onQuiz} data-testid="gate-quiz" className="w-full text-left mt-3">
            <Card className="flex items-start gap-3 p-5 border-2 border-km-border">
              <ClipboardList size={22} className="text-km-ink shrink-0 mt-0.5" />
              <div>
                <AppText variant="bodyStrong">Je réponds au questionnaire</AppText>
                <AppText variant="caption" className="text-km-muted mt-1">5 minutes, sans compte ni carte. On calcule ton profil à partir de tes réponses.</AppText>
              </div>
            </Card>
          </button>
        </div>
      </div>
    </div>
  );
}

